import dynamic from 'next/dynamic';

import type { ILinkedComponentFields } from 'types/generated/contentful';
import type { AverageScore } from 'lib/api-routes/ekomi/types';
import { isLive } from 'lib/environment/environmentHelper';

// Components that we always want to have ready to render
// are declared here and not dynamically imported.
import { UnknownComponent } from '../UnknownComponent/UnknownComponent';

const componentMap = {
  PrepackagedSeoContent: dynamic(() =>
    import('components/ContentfulContent/LinkedComponent/PrePackagedContent/PrePackagedSeaContent').then(
      (module) => module.PrePackagedSeaContent,
    ),
  ),
  PrepackagedSeoContentWithMinimalLegalExample: dynamic(() =>
    import('components/ContentfulContent/LinkedComponent/PrePackagedContent/PrepackagedSeoContentWithMinimalLegalExample').then(
      (module) => module.PrepackagedSeoContentWithMinimalLegalExample,
    ),
  ),
  PrepackagedEmailPartnerContentWithMinimalLegalExample: dynamic(() =>
    import('components/ContentfulContent/LinkedComponent/PrePackagedContent/PrepackagedEmailPartnerContentWithMinimalLegalExample').then(
      (module) => module.PrepackagedEmailPartnerContentWithMinimalLegalExample,
    ),
  ),
  ContentGoogleBlockAsteriskFooter: dynamic(() =>
    import('components/ContentfulContent/LinkedComponent/PrePackagedContent/ContentGoogleBlockAsteriskFooter').then(
      (module) => module.ContentGoogleBlockAsteriskFooter,
    ),
  ),
  EkomiWidgetLink: dynamic(() =>
    import('components/ContentfulContent/LinkedComponent/EkomiWidget/EkomiWidgetLink').then(
      (module) => module.EkomiWidgetLink,
    ),
  ),
  EkomiTestimonialsLink: dynamic(() =>
    import('components/ContentfulContent/LinkedComponent/EkomiTestimonials/EkomiTestimonialsLink').then(
      (module) => module.EkomiTestimonialsLink,
    ),
  ),
  PartnerBanksLink: dynamic(() =>
    import('components/ContentfulContent/LinkedComponent/PartnerBanks/PartnerBanksLink').then(
      (module) => module.PartnerBanksLink,
    ),
  ),
  TrustBannerLink: dynamic(() =>
    import('components/ContentfulContent/LinkedComponent/TrustBanner/TrustBannerLink').then(
      (module) => module.TrustBannerLink,
    ),
  ),
  AdvantagesLink: dynamic(() =>
    import('components/ContentfulContent/LinkedComponent/Advantages/Advantages').then(
      (module) => module.AdvantagesLink,
    ),
  ),
  StepExplanationsLink: dynamic(() =>
    import('components/ContentfulContent/LinkedComponent/StepExplanations/StepExplanationsLink').then(
      (module) => module.StepExplanationsLink,
    ),
  ),
  BottomNavigationMapLink: dynamic(() =>
    import('components/ContentfulContent/LinkedComponent/BottomNavigationMap/BottomNavigationMapLink').then(
      (module) => module.BottomNavigationMapLink,
    ),
  ),
  LegalTextGoogleFooterLink: dynamic(() =>
    import('components/ContentfulContent/LinkedComponent/LegalTexts/LegalTextGoogleFooterLink').then(
      (module) => module.LegalTextGoogleFooterLink,
    ),
  ),
  LegalTextGoogleTopLink: dynamic(() =>
    import('components/ContentfulContent/LinkedComponent/LegalTexts/LegalTextGoogleTopLink').then(
      (module) => module.LegalTextGoogleTopLink,
    ),
  ),
  LegalTextEmailPartnerFooterLink: dynamic(() =>
    import('components/ContentfulContent/LinkedComponent/LegalTexts/LegalTextEmailPartnerFooterLink').then(
      (module) => module.LegalTextEmailPartnerFooterLink,
    ),
  ),
};

export type LinkedComponentProps = ILinkedComponentFields & {
  ekomiScore?: AverageScore | null;
};

export const LinkedComponent = (props: LinkedComponentProps) => {
  const { configurationJson, ekomiScore } = props;

  const { component: componentName } = configurationJson.data;
  if (!componentName) return null;
  // @ts-ignore
  const Component = componentMap[componentName];

  if (!Component && isLive()) return null;
  if (!Component && !isLive()) return <UnknownComponent name={componentName} />;
  // @ts-ignore
  return <Component {...configurationJson.props} ekomiScore={ekomiScore} />;
};
